import { Loader } from "@googlemaps/js-api-loader";
import Glide from "@glidejs/glide";
import { Fancybox } from "@fancyapps/ui";
import NiceSelect from "nice-select2";
import Choices from "choices.js";
import Chart from "chart.js/auto";
import "chartjs-adapter-date-fns";
import Splide from "@splidejs/splide";
import { parseISO, format } from "date-fns";
import {
  propTrackChart,
  initPropTrackSelections,
  showContainer,
  createChartsForData
} from "./prop-track.js";
import { initSuburbSearch } from "./initSuburbSearch.js";

window.Chart = Chart;
window.parseISO = parseISO;
window.format = format;
window.createChartsForData = createChartsForData;

const loader = new Loader({
  apiKey: "AIzaSyAzf5ud3ISTNQM5mbbbdQXj7p0b0CxSw2c",
  libraries: ["places", "geometry"],
  version: "weekly"
});

function propertyReportPreviewMap(lat, lon, targetElement) {
  const resolvedTarget = targetElement instanceof Element
    ? targetElement
    : (typeof targetElement === "string" ? document.getElementById(targetElement) : document.getElementById("street-view"));

  if (!resolvedTarget) {
    console.warn("Street View target not found for property preview.");
    return;
  }

  const location = {
    lat: lat,
    lng: lon
  };
  loader.load().then(() => {
    const streetViewService = new google.maps.StreetViewService();
    const radius = 50; // Search radius in meters. Adjust if necessary.

    streetViewService.getPanorama({
      location: location,
      radius: radius
    }, (data, status) => {
      if (status === google.maps.StreetViewStatus.OK) { // The actual panorama location might be slightly off from the property coordinate.
        const panoramaLocation = data.location.latLng;

        // Compute the heading from the panorama location to the property.
        const propertyLatLng = new google.maps.LatLng(location.lat, location.lng);
        const heading = google.maps.geometry.spherical.computeHeading(panoramaLocation, propertyLatLng);

        // Initialize the Street View Panorama against the active cloned container rather than the first static id match.
        new google.maps.StreetViewPanorama(resolvedTarget, {
          position: panoramaLocation,
          pov: {
            heading: heading,
            pitch: 0
          },
          zoom: 1
        });
      } else {
        console.error("Street View data not found for this location");
      }
    });
  }).catch(e => {
    console.error("Failed to load Google Maps API", e);
  });
}

async function getLatLngFromAddress(address) {
  if (!address) return null;

  // Use Google Geocoding only
  try {
    await loader.load();
    if (typeof google !== 'undefined' && google && google.maps) {
      const geocoder = new google.maps.Geocoder();
      const googleResult = await new Promise((resolve) => {
        geocoder.geocode({ address: address, componentRestrictions: { country: 'au' } }, (results, status) => {
          if (status === 'OK' && results && results.length) {
            // Filter out broad/poi/tourist-style results unless they also include a street_number or street_address
            const excludeIfContains = [
              'tourist drive', 'tourist road', 'great ocean road', 'scenic drive',
              'highway', 'hwy', 'freeway', 'motorway'
            ];

            const excludeTypes = new Set([
              'route', 'natural_feature', 'tourist_attraction',
              'point_of_interest', 'establishment', 'colloquial_area'
            ]);

            const isGoodResult = (r) => {
              const types = r.types || [];
              const formatted = (r.formatted_address || '').toLowerCase();
              const hasStreetNumber = r.address_components && r.address_components.some(ac => ac.types && ac.types.includes('street_number'));
              const isStreetAddress = types.includes('street_address') ||
                (r.address_components && r.address_components.some(ac => ac.types && ac.types.includes('street_address')));

              // Reject obvious tourist / highway results by phrase
              if (excludeIfContains.some(phrase => formatted.includes(phrase))) {
                return false;
              }

              // If result has an excluded broad type and *no* street number or explicit street_address, reject it
              if (types.some(t => excludeTypes.has(t)) && !hasStreetNumber && !isStreetAddress) {
                return false;
              }

              // Accept otherwise
              return true;
            };

            // Prefer results that are explicit street_address or that include a street_number
            const preferred = results.find(r =>
              (r.types && r.types.includes('street_address')) ||
              (r.address_components && r.address_components.some(ac => ac.types && ac.types.includes('street_number')))
            );

            let chosen = null;

            if (preferred && isGoodResult(preferred)) {
              chosen = preferred;
            } else {
              // try to pick the first result that passes the "isGoodResult" filter
              const filtered = results.filter(isGoodResult);
              chosen = filtered[0] || results[0];
            }

            const loc = chosen.geometry && chosen.geometry.location;
            if (loc) {
              // Try to extract a street_address component (fallback to route + number)
              let streetAddress = null;
              if (chosen.address_components && Array.isArray(chosen.address_components)) {
                const streetComp = chosen.address_components.find(ac => ac.types.includes('street_address'));
                if (streetComp) {
                  streetAddress = streetComp.long_name;
                } else {
                  const numberComp = chosen.address_components.find(ac => ac.types.includes('street_number'));
                  const routeComp = chosen.address_components.find(ac => ac.types.includes('route'));
                  if (routeComp) {
                    streetAddress = (numberComp ? numberComp.long_name + ' ' : '') + routeComp.long_name;
                  }
                }
              }

              resolve({
                lat: loc.lat(),
                lng: loc.lng(),
                raw: chosen,
                street_address: streetAddress,
                formatted_address: chosen.formatted_address || null
              });
              return;
            }
          }
          resolve(null);
        });
      });

      if (googleResult) {
        return { ...googleResult, source: 'google' };
      }
    }
  } catch (err) {
    /* eslint-disable no-console */
    console.warn('Google geocode failed or not available:', err);
    /* eslint-enable no-console */
  }

  return null;
}

/**
 * Show the listings on the map. Used on the sold listings page.
 * The 'property_{status}_data' object is created in the WordPress template.
 */
function soldListingMaps() {
  const data = {
    option: ["current", "rent", "sold"],
    count: {},
    map: {},
    mapContainer: {},
    btn: {},
    tab: {},
    results: document.querySelector(
      "#suburb-listings-section #result-count span"
    ),
    title: document.querySelector(
      "#suburb-listings-section #suburb-listings-title span"
    )
  };

  // Assign the data to the correct option.
  data.option.forEach(opt => {
    const optLower = opt.toLowerCase();
    const value = window[`property_${optLower}_data`];
    if (value.properties.length > 0) {
      data.count[opt] = value.properties.length;
      data.map[opt] = understrap.historyMap(value, `${opt}-suburb-map`);
      data.mapContainer[opt] = document.querySelector(`#${opt}-suburb-map`);
      data.btn[opt] = document.querySelector(`#view-all-${opt}-btn`);
      data.tab[opt] = document.querySelector(`#${opt}-listings-tab`);
    }
  });

  if (!data.map.current) return;

  data.title.innerHTML = "For Sale";
  data.results.innerHTML = data.count.current;

  if (data.count.current > 0) {
    data.btn.current.style.display = "block";
  }

  data.option.forEach(opt => {
    if (!data.tab[opt]) {
      return;
    }

    data.tab[opt].addEventListener("click", function () {
      for (let [key, value] of Object.entries(data.mapContainer)) {
        if (key === opt) {
          if (data.count[opt] > 0) {
            value.classList.add("active");
            value.classList.remove("d-none");
          }
        } else {
          value.classList.remove("active");
          value.classList.add("d-none");
        }
      }

      if (!data.map[opt]) return;
      let title = opt.charAt(0).toUpperCase() + opt.slice(1);
      if (title === "Current") {
        title = "For Sale";
      }
      if (title === "Rent") {
        title = "For Rent";
      }
      data.title.innerHTML = title;
      data.results.innerHTML = data.count[opt];

      if (data.count[opt] > 0) {
        data.btn[opt].style.display = "block";
      }
    });
  });
}

/**
 * Process the properties and fetch the coordinates for each property.
 * 
 * @param {array} listingArray - Array of listings to show on the map.
 * return {array} locations - Array of locations with coordinates.
 */
async function processProperties(listingArray) {
  const endpoint = "https://nominatim.openstreetmap.org/search?q=";
  const query = "&format=json&limit=1";
  const locations = [];

  // Loop through the listings and fetch the coordinates for each property.
  for (const property of listingArray.properties) {
    // Check if the property has coordinates already, if so, skip the API call.
    if (property.coordinates !== "") {
      locations.push([
        property.title,
        property.coordinates.lat,
        property.coordinates.lng
      ]);
      break;
    }
    // Fetch the coordinates from the API for listings without coordinates.
    const response = await fetch(
      endpoint + encodeURIComponent(property.title) + query
    )
      .then(response => response.json())
      .then(data => {
        // Check if the data is empty.
        if (data.length === 0) {
          return;
        }
        const { lat, lon: lng } = data[0];
        locations.push([property.title, lat, lng]);
      });
  }
  return locations;
}

/**
 * Show the Suburb Map with listings on sold listing pages.
 * @param {array} listingArray - Array of listings to show on the map.
 * @param {string} mapID - ID of the map to show the listings on.
 * return {void}
 * 
 */
async function historyMap(listingArray, containerId) {
  /**
   * Set the execution conditions. The following must be met:
   * - Must be a single listing page.
   * - The property must be sold.
   * - The Google Maps API must exist.
   * - There must be listings to show (fed to this function via listingArray).
   * - The map container must exist on the page.
   */
  if (typeof property_status === "undefined") {
    return false;
  }
  // Exit if the property is not sold.
  if (property_status !== "sold") {
    return false;
  }
  // Exit if Google Maps is not loaded.
  if (!google.maps) {
    return false;
  }
  // Exit if there are no listings to show.
  if (!listingArray) {
    return false;
  }
  // Check if the map exists on the page.
  const mapContainer = document.getElementById(containerId);
  if (!mapContainer) {
    return false;
  }
  // Process the properties and fetch the coordinates for each property.
  // Waits for the processProperties function to complete before continuing.
  const pins = await processProperties(listingArray);
  // Exit if there are no pins to show.
  if (pins.length === 0) {
    return false;
  }
  // Create the map and show the listings.
  const map = new google.maps.Map(mapContainer, {
    zoom: 10,
    center: new google.maps.LatLng(pins[0][1], pins[0][2]),
    mapTypeId: google.maps.MapTypeId.ROADMAP
  });

  // Show the pins on the map.
  const infoWindow = new google.maps.InfoWindow();

  pins.forEach(pin => {
    const marker = new google.maps.Marker({
      position: new google.maps.LatLng(pin[1], pin[2]),
      map: map
    });

    // Show the info window when the marker is clicked.
    google.maps.event.addListener(marker, "click", () => {
      infoWindow.setContent(pin[0]);
      infoWindow.open(map, marker);
    });
  });
}

async function initMap() {

  const options = {
    componentRestrictions: {
      country: "au"
    },
    fields: ["address_components", "geometry", "icon", "name"],
    strictBounds: false,
    types: ["address"]
  };

  loader.load().then(async () => {
    const { Autocomplete } = await google.maps.importLibrary("places");

    const autocompleteInputs = document.querySelectorAll(
      '.autocomplete-address input[type="text"]'
    );
    autocompleteInputs.forEach(function (input) {
      new Autocomplete(input, options);
    });
  });
}

function commissionCalculator() {
  $ = jQuery;
  var format = function (num) {
    var str = num.toString().replace("$", ""),
      parts = false,
      output = [],
      i = 1,
      formatted = null;
    if (str.indexOf(".") > 0) {
      parts = str.split(".");
      str = parts[0];
    }
    str = str.split("").reverse();
    for (var j = 0, len = str.length; j < len; j++) {
      if (str[j] != ",") {
        output.push(str[j]);
        if (i % 3 == 0 && j < len - 1) {
          output.push(",");
        }
        i++;
      }
    }
    formatted = output.reverse().join("");
    return "$" + formatted + (parts ? "." + parts[1].substr(0, 2) : "");
  };

  /**
   * Calculate based on commission calculator spreadsheet provided by Image Property.
   * Calculate the Commission Brackets:
   * * 70% for every dollar under $100k (gci_70).
   * 80% for every dollar between $100k - $200k (gci_80).
   * 100% commission for every dollar above $200k.
   * @TODO fix all this to be easier to read.
   */

  const cgi = document.getElementById("total_gci_per_annum");
  const sales_per_annum = document.getElementById("sales_per_annum");
  const gross_commission = document.getElementById("gross_commission");
  const sub_fee = document.getElementById("Sub_fee");
  const collapseExample = document.getElementById("collapseExample");
  const calculateButton = document.getElementById("calculate");
  const settlement_fee = document.getElementById("settlement_fee");
  const total_commission = document.getElementById("total_commission");
  const resetButton = document.getElementById("show");

  let gci_70 = 0;
  let gci_80 = 0;
  let gci_100 = 0;

  resetButton.addEventListener("click", function () {
    calculateButton.style.display = "";
  });

  calculateButton.addEventListener("click", function () {
    calculateButton.style.display = "none";

    let cgiVal = cgi.value !== "" ? parseInt(cgi.value) : 0;

    if (cgiVal >= 100000) {
      gci_70 = 70000;
    } else {
      gci_70 = cgiVal * 0.7;
    }

    if (cgiVal >= 100000 && cgiVal <= 200000) {
      gci_80 = (cgiVal - 100000) * 0.8;
    } else if (cgiVal >= 200000) {
      gci_80 = 80000;
    }

    if (cgiVal >= 200000) {
      gci_100 = cgiVal - 200000;
    } else {
      gci_100 = (cgiVal - 200000) * 0.8;

      if (gci_100 <= 0) {
        gci_100 = 0;
      }
    }

    let gross = gci_70 + gci_80 + gci_100;
    let spaVal =
      sales_per_annum.value !== "" ? parseInt(sales_per_annum.value) : 0;
    let total_sub_fee = 500 * 12;
    let settlement = 695 * spaVal;
    let net_commission =
      parseInt(gross) - (parseInt(settlement) + parseInt(total_sub_fee));

    gross_commission.value = gross;
    sub_fee.value = total_sub_fee;
    settlement_fee.value = settlement;
    total_commission.innerHTML = format(net_commission);
    collapseExample.classList.add("show");
  });
}

document.addEventListener("DOMContentLoaded", function (event) {
  initMap();

  const listingSort = document.querySelector(".epl-sort-listings");
  listingSort &&
    new Choices(listingSort, {
      searchEnabled: false,
      itemSelectText: "",
      shouldSort: false
    });

  const propertyCategory = document.querySelector("#property_category");
  propertyCategory &&
    new Choices(propertyCategory, {
      searchEnabled: false,
      searchChoices: false,
      itemSelectText: "",
      closeDropdownOnSelect: true,
      removeItemButton: true,
      placeholder: true,
      placeholderValue: "Select property types"
    });

  const propertyPriceFrom = document.querySelector("#property_price_from");
  propertyPriceFrom &&
    new Choices(propertyPriceFrom, {
      searchEnabled: false,
      shouldSort: false,
      searchChoices: false,
      itemSelectText: ""
    });

  const propertyPriceTo = document.querySelector("#property_price_to");
  propertyPriceTo &&
    new Choices(propertyPriceTo, {
      searchEnabled: false,
      shouldSort: false,
      searchChoices: false,
      itemSelectText: ""
    });

  const listingSuburbSearchFilter = document.querySelector(
    "#property_location"
  );

  listingSuburbSearchFilter &&
    new Choices(listingSuburbSearchFilter, {
      searchEnabled: true,
      searchChoices: true,
      itemSelectText: "",
      shouldSort: true,
      placeholder: true,
      renderChoiceLimit: 8,
      closeDropdownOnSelect: true,
      placeholderValue: "Search by suburb",
      searchPlaceholderValue: "Search by suburb",
      removeItemButton: true,
      classNames: {
        containerInner: ["inner__container"],
        inputCloned: ["choices__input--cloned"],
        list: ["choices__list"],
        listItems: ["choices__list--multiple"],
        listSingle: ["choices__list--single"],
        listDropdown: ["choices__list--dropdown"],
        item: ["choices__item"],
        itemSelectable: ["choices__item--selectable"],
        itemDisabled: ["choices__item--disabled"],
        itemChoice: ["choices__item--choice"],
        description: ["choices__description"],
        placeholder: ["choices__placeholder"],
        group: ["choices__group"],
        groupHeading: ["choices__heading"],
        button: ["choices__button"],
        activeState: ["is-active"],
        focusState: ["is-focused"],
        openState: ["is-open"],
        disabledState: ["is-disabled"],
        highlightedState: ["is-highlighted"],
        selectedState: ["badge rounded-pill text-bg-secondary"],
        flippedState: ["is-flipped"],
        loadingState: ["is-loading"],
        notice: ["choices__notice"],
        addChoice: ["choices__item--selectable", "add-choice"],
        noResults: ["has-no-results"],
        noChoices: ["has-no-choices"]
      }
    });
});

// Splide
const arrowSVGPath =
  "M11.6197 4.11683C11.7358 4.00042 11.8737 3.91734 12.0322 3.84505C12.1907 3.77276 12.3669 3.7496 12.5447 3.7496C12.7225 3.7496 12.8987 3.77276 13.0572 3.84505C13.2157 3.91734 13.3536 4.00042 13.4697 4.11683L28.4697 19.1168C28.5858 19.2332 28.6689 19.3711 28.7412 19.5296C28.8135 19.6881 28.8367 19.8643 28.8367 20.0421C28.8367 20.2199 28.8135 20.3961 28.7412 20.5546C28.6689 20.7131 28.5858 20.851 28.4697 20.9671L13.4697 35.9671C13.227 36.2098 12.8366 36.2533 12.5447 36.2533C12.2528 36.2533 11.8624 36.2098 11.6197 35.9671C11.377 35.7244 11.3335 35.334 11.3335 35.0421C11.3335 34.7502 11.377 34.3598 11.6197 34.1171L25.7372 20.0421L11.6197 5.9671C11.5033 5.85099 11.4202 5.7131 11.3479 5.5546C11.2756 5.3961 11.2524 5.2199 11.2524 5.0421C11.2524 4.8643 11.2756 4.6881 11.3479 4.5296C11.4202 4.3711 11.5033 4.2332 11.6197 4.11683Z";
if (document.querySelector(".splide")) {
  new Splide(".rcreviews--section", {
    perPage: 3,
    perMove: 1,
    rewind: true,
    gap: "32px",
    pagination: false,
    arrowPath: arrowSVGPath,
    breakpoints: {
      1151: {
        perPage: 1
      }
    }
  }).mount();
}

// RC Reviews
document.addEventListener("DOMContentLoaded", function () {
  "use strict";

  const contentElements = document.querySelectorAll(".rcreviews--content");

  if (contentElements.length > 0) {
    contentElements.forEach(contentElement => {
      contentElement.classList.remove("rcreviews--truncate");
      const readMoreElementWrapper = contentElement.nextElementSibling;

      if (
        readMoreElementWrapper &&
        readMoreElementWrapper.classList.contains(
          "rcreviews--read-more-wrapper"
        )
      ) {
        const lineHeight = parseFloat(
          getComputedStyle(contentElement).lineHeight
        );
        const contentHeight = contentElement.clientHeight;
        const lines = contentHeight / lineHeight;

        if (lines <= 3) {
          readMoreElementWrapper.remove();
        } else {
          contentElement.classList.add("rcreviews--truncate");
        }
      }
    });
  }
});

document.addEventListener("DOMContentLoaded", function () {
  document
    .querySelectorAll(".section--cta-carousel")
    .forEach(function (content, index) {
      const splideElement = content.querySelector(".splide"); // Select Splide inside this section
      const prevButton = content.querySelector(".control-left"); // Select the left control in this section
      const nextButton = content.querySelector(".control-right"); // Select the right control in this section

      if (!splideElement) return; // Skip if no Splide instance found

      const splide = new Splide(splideElement, {
        type: "loop",
        perPage: 1,
        pagination: false,
        arrows: false, // Disable built-in arrows since we're using custom ones
        autoHeight: true,
        autoWidth: false
      }).mount();

      // Custom Navigation using Controller API
      if (prevButton) {
        prevButton.addEventListener("click", function () {
          splide.go("-1"); // Go to previous slide
        });
      }

      if (nextButton) {
        nextButton.addEventListener("click", function () {
          splide.go("+1"); // Go to next slide
        });
      }
    });

  const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
  const tooltipList = [...tooltipTriggerList].map(tooltipTriggerEl => new understrap.Tooltip(tooltipTriggerEl));
});

// Popup trigger for invalid suburb
document.addEventListener('DOMContentLoaded', () => {
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('popup') === 'invalid_address') {
    Fancybox.show([
      {
        src: '#invalid-suburb-popup',
        type: 'inline',
      },
    ]);

    //remove the query param after showing popup
    window.history.replaceState({}, document.title, '/suburb-report/');
  }
});

export {
  // propertySearchForm,
  commissionCalculator,
  Glide,
  historyMap,
  soldListingMaps,
  propTrackChart,
  initPropTrackSelections,
  NiceSelect,
  Fancybox,
  Splide,
  initSuburbSearch,
  loader,
  propertyReportPreviewMap,
  getLatLngFromAddress
};
